import { useState } from 'react'
import { InvoiceCompareBar } from '@/components/InvoiceCompareBar'
import {
  INVOICE_COMPARE_FIELDS,
  differingInvoiceFields,
  invoiceFieldDiffers,
  type CustomerInvoiceValues,
  type InvoiceCompareKey,
} from '@/lib/invoiceCustomerDiff'
import type { ComponentProps } from 'react'
import { CalloutNotesSection } from '@/components/CalloutNotesSection'
import { LinkedCustomer } from '@/components/LinkedCustomer'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { type SearchOption } from '@/components/ui/Search'
import { TimeSelect } from '@/components/ui/time-select'
import { UnderlineDateInput } from '@/components/ui/underline-date-input'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import type { CalloutStatus } from '@/db/domain/calloutStatus'
import { toWhatsAppHref } from '@/lib/whatsapp'

type SectionActionsProps = {
  saveEnabled: boolean
  onSave: () => void
  onReset?: () => void
}

const saveButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-slate-100 text-slate-500',
  ].join(' ')

const resetButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      : 'bg-slate-100 text-slate-400',
  ].join(' ')

/**
 * A section's Reset/Save pair.
 *
 * Both the enabled look and the `disabled` attribute come from the single
 * `saveEnabled` flag, so a button can never look clickable while being inert —
 * which is exactly what happened on the job page when the two were computed
 * from different expressions.
 */
export function SectionActions(props: SectionActionsProps) {
  const { saveEnabled, onSave, onReset } = props
  return (
    <div className="flex items-center gap-2">
      {onReset ? (
        <button
          type="button"
          className={resetButtonClasses(saveEnabled)}
          disabled={!saveEnabled}
          onClick={onReset}
        >
          Reset
        </button>
      ) : null}
      <button
        type="button"
        className={saveButtonClasses(saveEnabled)}
        disabled={!saveEnabled}
        onClick={onSave}
      >
        Save
      </button>
    </div>
  )
}

export function MobileVisitDetailsCard(props: {
  address: string
  contactName: string
  contactPhone: string
}) {
  const { address, contactName, contactPhone } = props

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2 md:hidden">
      <FormCapsLabel>Quick Contact</FormCapsLabel>
      <p className="text-sm text-slate-700">
        <span className="text-slate-900">{contactName}</span> / <span>{contactPhone}</span>
      </p>
      <p className="text-sm text-slate-700">{address}</p>
      <div className="flex gap-2 pt-1">
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank"
          rel="noreferrer noopener"
          className="flex-1 flex items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2.5 text-sm font-medium text-white active:bg-blue-700"
        >
          Navigate
        </a>
        <a
          href={toWhatsAppHref(contactPhone)}
          target="_blank"
          rel="noreferrer noopener"
          className="flex-1 flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 py-2.5 text-sm font-medium text-white active:bg-emerald-700"
        >
          WhatsApp
        </a>
      </div>
    </div>
  )
}

type CustomerSectionProps = {
  customerName: string
  customerId?: string
  customerPublicId?: string
  selectedCustomer: SearchOption | null
  isImmutable: boolean
  saveEnabled: boolean
  details?: {
    fullName?: string
    externalId?: string
    contactEmail?: string
    contactPhone?: string
    contactAddress?: string
    invoiceName?: string
    invoiceEmail?: string
    invoicePhone?: string
    invoiceAddress?: string
    invoiceVat?: string
  }
  onSelectCustomer: (option: SearchOption | null) => void
  onSaveCustomer: () => void
  onResetCustomer: () => void
}

export function CustomerSection(props: CustomerSectionProps) {
  const {
    customerName,
    customerId,
    customerPublicId,
    selectedCustomer,
    isImmutable,
    saveEnabled,
    details,
    onSelectCustomer,
    onSaveCustomer,
    onResetCustomer,
  } = props

  return (
    <LinkedCustomer
      mode="edit"
      customerName={customerName}
      customerId={customerId}
      customerPublicId={customerPublicId}
      selectedCustomer={selectedCustomer}
      isImmutable={isImmutable}
      saveEnabled={saveEnabled}
      details={details}
      onSelectCustomer={onSelectCustomer}
      onSaveCustomer={onSaveCustomer}
      onResetCustomer={onResetCustomer}
    />
  )
}

type BookingSectionProps = {
  day: string
  wholeDay: boolean
  displayStartTime: string
  displayEndTime: string
  isImmutable: boolean
  saveEnabled: boolean
  onDayChange: (value: string) => void
  onWholeDayChange: (checked: boolean) => void
  onStartChange: (value: string) => void
  onEndChange: (value: string) => void
  onSave: () => void
  onReset: () => void
}

export function BookingSection(props: BookingSectionProps) {
  const {
    day,
    wholeDay,
    displayStartTime,
    displayEndTime,
    isImmutable,
    saveEnabled,
    onDayChange,
    onWholeDayChange,
    onStartChange,
    onEndChange,
    onSave,
    onReset,
  } = props
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FormCapsLabel>Booking details</FormCapsLabel>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-slate-900 accent-slate-900"
              checked={wholeDay}
              disabled={isImmutable}
              onChange={(event) => onWholeDayChange(event.target.checked)}
            />
            <span>Whole day</span>
          </label>
        </div>
        <SectionActions
          saveEnabled={saveEnabled}
          onSave={onSave}
          onReset={onReset}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 text-xs">
          <FormFieldLabel className="w-32">Day</FormFieldLabel>
          <UnderlineDateInput
            value={day}
            disabled={isImmutable}
            onChange={(event) => onDayChange(event.target.value)}
          />
        </div>
        <div className="space-y-1 text-xs sm:col-span-2">
          <FormFieldLabel className="w-32">Time window</FormFieldLabel>
          <div className="flex flex-wrap items-center gap-3">
            <TimeSelect
              className="max-w-[8rem]"
              value={displayStartTime}
              disabled={isImmutable || wholeDay}
              onChange={(event) => onStartChange(event.target.value)}
            />
            <span className="text-[11px] text-slate-500">to</span>
            <TimeSelect
              className="max-w-[8rem]"
              value={displayEndTime}
              disabled={isImmutable || wholeDay}
              onChange={(event) => onEndChange(event.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

type IssueSectionProps = {
  issueText: string
  isImmutable: boolean
  saveEnabled: boolean
  onChange: (value: string) => void
  onSave: () => void
  onReset: () => void
}

export function IssueSection(props: IssueSectionProps) {
  const { issueText, isImmutable, saveEnabled, onChange, onSave, onReset } =
    props
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <FormCapsLabel>Issue</FormCapsLabel>
        <SectionActions
          saveEnabled={saveEnabled}
          onSave={onSave}
          onReset={onReset}
        />
      </div>
      <UnderlineTextarea
        value={issueText}
        disabled={isImmutable}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Describe the issue to investigate"
        rows={3}
      />
    </div>
  )
}

type StatusSectionProps = {
  statusSelection: CalloutStatus
  availableStatuses: CalloutStatus[]
  formatStatus: (value: CalloutStatus) => string
  onApplyStatus: (value: CalloutStatus, reason?: string) => void | Promise<void>
}

export function StatusSection(props: StatusSectionProps) {
  const {
    statusSelection,
    availableStatuses,
    formatStatus,
    onApplyStatus,
  } = props
  const [isSaving, setIsSaving] = useState(false)

  const runStatusAction = async (nextStatus: CalloutStatus) => {
    if (isSaving) return
    setIsSaving(true)
    try {
      await Promise.resolve(onApplyStatus(nextStatus))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <FormCapsLabel>Status actions</FormCapsLabel>
        <span className="text-[11px] text-slate-500">Current: {formatStatus(statusSelection)}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {availableStatuses.length === 0 ? (
          <p className="text-[11px] text-slate-500">No further status changes allowed from here.</p>
        ) : (
          availableStatuses.map((nextStatus) => (
            <button
              key={nextStatus}
              type="button"
              className={
                nextStatus === 'Cancelled'
                  ? 'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'
                  : nextStatus === 'ReadyToInvoice' || nextStatus === 'Invoiced'
                    ? 'inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                    : 'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'
              }
              disabled={isSaving}
              onClick={() => runStatusAction(nextStatus)}
            >
              {isSaving ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : null}
              {nextStatus === 'Cancelled' ? 'Cancel service' : `Move to ${formatStatus(nextStatus)}`}
            </button>
          ))
        )}
      </div>

    </div>
  )
}

type ContactSectionProps = {
  contactAddress: string
  contactName: string
  contactPhone: string
  contactEmail: string
  fieldOrder?: Array<'contactName' | 'contactPhone' | 'contactEmail' | 'contactAddress'>
  title?: string
  addressLabel?: string
  nameLabel?: string
  phoneLabel?: string
  emailLabel?: string
  isImmutable: boolean
  saveEnabled: boolean
  onChange: (fields: {
    contactAddress?: string
    contactName?: string
    contactPhone?: string
    contactEmail?: string
  }) => void
  onSave: () => void
  onReset: () => void
}

export function ContactSection(props: ContactSectionProps) {
  const {
    contactAddress,
    contactName,
    contactPhone,
    contactEmail,
    fieldOrder = ['contactAddress', 'contactName', 'contactPhone', 'contactEmail'],
    title = 'Contact',
    addressLabel = 'Visit address',
    nameLabel = 'Site contact',
    phoneLabel = 'Phone',
    emailLabel = 'Email',
    isImmutable,
    saveEnabled,
    onChange,
    onSave,
    onReset,
  } = props

  const fields = {
    contactAddress: (
      <div key="contactAddress" className="space-y-1">
        <FormFieldLabel className="w-32">{addressLabel}</FormFieldLabel>
        <UnderlineTextarea
          value={contactAddress}
          disabled={isImmutable}
          onChange={(event) =>
            onChange({ contactAddress: event.target.value })
          }
          placeholder="123 Main Road, Business Park"
          rows={2}
        />
      </div>
    ),
    contactName: (
      <div key="contactName" className="space-y-1">
        <FormFieldLabel className="w-32">{nameLabel}</FormFieldLabel>
        <UnderlineInput
          value={contactName}
          disabled={isImmutable}
          onChange={(event) =>
            onChange({ contactName: event.target.value })
          }
          placeholder="Name"
        />
      </div>
    ),
    contactPhone: (
      <div key="contactPhone" className="space-y-1">
        <FormFieldLabel className="w-32">{phoneLabel}</FormFieldLabel>
        <UnderlineInput
          value={contactPhone}
          disabled={isImmutable}
          onChange={(event) =>
            onChange({ contactPhone: event.target.value })
          }
          placeholder="+1 555 123 4567"
        />
      </div>
    ),
    contactEmail: (
      <div key="contactEmail" className="space-y-1">
        <FormFieldLabel className="w-32">{emailLabel}</FormFieldLabel>
        <UnderlineInput
          value={contactEmail}
          disabled={isImmutable}
          onChange={(event) =>
            onChange({ contactEmail: event.target.value })
          }
          placeholder="contact@example.com"
        />
      </div>
    ),
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <FormCapsLabel>{title}</FormCapsLabel>
        <SectionActions
          saveEnabled={saveEnabled}
          onSave={onSave}
          onReset={onReset}
        />
      </div>
      <div className="space-y-2 text-xs">
        {fieldOrder.map((fieldKey) => fields[fieldKey])}
      </div>
    </div>
  )
}

const canonDisplay = (value: string | undefined) =>
  value && value.trim() ? value.trim() : '(empty)'

type InvoiceSectionProps = {
  invoiceName: string
  invoiceEmail: string
  invoicePhone: string
  invoiceAddress: string
  invoiceVat: string
  invoiceId: string
  isImmutable: boolean
  saveEnabled: boolean
  /**
   * The customer's current invoice values. When given, every field shows how
   * it compares, with per-field and copy-all actions to pull them in.
   */
  customerInvoice?: CustomerInvoiceValues
  onChange: (fields: {
    invoiceName?: string
    invoiceEmail?: string
    invoicePhone?: string
    invoiceAddress?: string
    invoiceVat?: string
    invoiceId?: string
  }) => void
  onSave: () => void
  onReset: () => void
}

export function InvoiceSection(props: InvoiceSectionProps) {
  const {
    invoiceName,
    invoiceEmail,
    invoicePhone,
    invoiceAddress,
    invoiceVat,
    invoiceId,
    isImmutable,
    saveEnabled,
    customerInvoice,
    onChange,
    onSave,
    onReset,
  } = props

  const [showDiffs, setShowDiffs] = useState(false)
  const current: CustomerInvoiceValues = {
    invoiceName,
    invoiceEmail,
    invoicePhone,
    invoiceAddress,
    invoiceVat,
  }
  const differing = differingInvoiceFields(current, customerInvoice)

  /** Detail under one field — only for differing ones, only when expanded. */
  const compare = (key: InvoiceCompareKey) => {
    if (!customerInvoice || !showDiffs) return null
    if (!invoiceFieldDiffers(current[key], customerInvoice[key], key)) return null
    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        data-testid={`invoice-diff-${key}`}
      >
        <span>
          Customer has:{' '}
          <strong className="whitespace-pre-line">
            {canonDisplay(customerInvoice[key])}
          </strong>
        </span>
        <button
          type="button"
          disabled={isImmutable}
          title={
            isImmutable
              ? 'Amend the record to change this'
              : "Fill this field with the customer's value (review, then save)"
          }
          className="cursor-pointer rounded border border-amber-300 bg-white px-1.5 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => onChange({ [key]: customerInvoice[key] ?? '' })}
        >
          Use customer's
        </button>
      </div>
    )
  }

  const copyAll = () => {
    if (!customerInvoice) return
    const next: Partial<Record<InvoiceCompareKey, string>> = {}
    for (const field of differing) next[field.key] = customerInvoice[field.key] ?? ''
    onChange(next)
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <FormCapsLabel>Invoice details</FormCapsLabel>
        <SectionActions
          saveEnabled={saveEnabled}
          onSave={onSave}
          onReset={onReset}
        />
      </div>
      {customerInvoice ? (
        <InvoiceCompareBar
          differing={differing.length}
          total={INVOICE_COMPARE_FIELDS.length}
          expanded={showDiffs}
          onToggle={() => setShowDiffs((open) => !open)}
          onCopyAll={copyAll}
          copyDisabled={isImmutable}
          copyTitle={
            isImmutable
              ? 'Amend the record to change this'
              : 'Fill every differing field from the customer (review, then save)'
          }
        />
      ) : null}
      <div className="space-y-2 text-xs">
        <div className="space-y-1">
          <FormFieldLabel className="w-32">Invoice to</FormFieldLabel>
          <UnderlineInput
            value={invoiceName}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoiceName: event.target.value })
            }
            placeholder="Invoicing name"
          />
          {compare('invoiceName')}
        </div>
        <div className="space-y-1">
          <FormFieldLabel className="w-32">Invoice email</FormFieldLabel>
          <UnderlineInput
            value={invoiceEmail}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoiceEmail: event.target.value })
            }
            placeholder="accounts@example.com"
          />
          {compare('invoiceEmail')}
        </div>
        <div className="space-y-1">
          <FormFieldLabel className="w-32">Invoice phone</FormFieldLabel>
          <UnderlineInput
            value={invoicePhone}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoicePhone: event.target.value })
            }
            placeholder="082 000 0000"
          />
          {compare('invoicePhone')}
        </div>
        <div className="space-y-1">
          <FormFieldLabel className="w-32">Invoice address</FormFieldLabel>
          <UnderlineTextarea
            value={invoiceAddress}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoiceAddress: event.target.value })
            }
            placeholder="Billing address"
            rows={2}
          />
          {compare('invoiceAddress')}
        </div>
        <div className="space-y-1">
          <FormFieldLabel className="w-32">VAT number</FormFieldLabel>
          <UnderlineInput
            value={invoiceVat}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoiceVat: event.target.value })
            }
            placeholder="VAT / Tax ID"
          />
          {compare('invoiceVat')}
        </div>
        <div className="space-y-1">
          <FormFieldLabel className="w-32">Invoice number (Omni)</FormFieldLabel>
          <UnderlineInput
            value={invoiceId}
            disabled={isImmutable}
            onChange={(event) =>
              onChange({ invoiceId: event.target.value })
            }
            placeholder="INV-12345"
          />
        </div>
      </div>
    </div>
  )
}

type NotesSectionProps = {
  notes: ComponentProps<typeof CalloutNotesSection>['notes']
  newNoteAuthorName: string
  isImmutable: boolean
  onAddNote?: (text: string) => boolean
  onUpdateNote?: (id: string, patch: { text?: string; caption?: string }) => void
  onDeleteNote?: (id: string) => void
}

export function NotesSection(props: NotesSectionProps) {
  const {
    notes,
    newNoteAuthorName,
    isImmutable,
    onAddNote,
    onUpdateNote,
    onDeleteNote,
  } = props
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)

  const canEditNotes = !isImmutable && Boolean(onUpdateNote)
  const canDeleteNotes = !isImmutable && Boolean(onDeleteNote)

  const handleStartEditNote = (id: string) => {
    if (!canEditNotes) return
    setEditingNoteId(id)
  }

  const handleCancelEditNote = () => {
    setEditingNoteId(null)
  }

  const handleSaveEditNote = (id: string, textOrCaption: string) => {
    const targetNote = notes.find((note) => note.id === id)
    if (!targetNote || !onUpdateNote) return

    if (targetNote.kind === 'photo') {
      onUpdateNote(id, { caption: textOrCaption })
    } else {
      onUpdateNote(id, { text: textOrCaption })
    }
    setEditingNoteId(null)
  }

  const handleDeleteNote = (id: string) => {
    if (!onDeleteNote) return
    onDeleteNote(id)
    if (editingNoteId === id) {
      setEditingNoteId(null)
    }
  }

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <FormCapsLabel>Notes</FormCapsLabel>
        <button
          type="button"
          className={saveButtonClasses(false)}
          disabled
        >
          Save
        </button>
      </div>
      <CalloutNotesSection
        notes={notes}
        newNoteAuthorName={newNoteAuthorName}
        showNewNoteArea={!isImmutable && Boolean(onAddNote)}
        onAddNote={onAddNote}
        editingNoteId={editingNoteId}
        onStartEditNote={canEditNotes ? handleStartEditNote : undefined}
        onCancelEditNote={canEditNotes ? handleCancelEditNote : undefined}
        onSaveEditNote={canEditNotes ? handleSaveEditNote : undefined}
        onDeleteNote={canDeleteNotes ? handleDeleteNote : undefined}
      />
    </section>
  )
}
